import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { FormFieldSet } from "@progress/kendo-react-all";
import { ControlledCheckbox } from "@excent-one/react/form";
import { ControlledFileUploader } from "@excent-one/react/form/inputs";
import { dialogBox } from "@excent-one/react/dialog-box";
import { checkIcon } from "@progress/kendo-svg-icons";
import { resolveIncidentAttachmentUrl } from "@phrmpc/shared/hooks/useIncidentAttachmentContent";
import type { IncidentRecordFileInfoDto } from "@phrmpc/infrastructure/phrmpc/models";
import type { CaseDocumentChainSlot } from "../hooks/useCaseDocumentChain";
import {
  CASE_DOCUMENT_ALLOWED_EXTENSIONS,
  CASE_DOCUMENT_MAX_SIZE_BYTES,
} from "../utils/uploadLimits";

interface SlotFormValues {
  unsignedFile: IncidentRecordFileInfoDto | null;
  signedFile: IncidentRecordFileInfoDto | null;
  acknowledged: boolean;
}

const emptySlotValues: SlotFormValues = {
  unsignedFile: null,
  signedFile: null,
  acknowledged: false,
};

/**
 * A slot's currently-pending confirm action, if any - surfaced by `getPendingSubmit` so the
 * parent page can render a single "Submit" button in its FormActionsBar (alongside Save/Complete/
 * Back) instead of one inline per tab (per BA feedback: Submit is a page-level action, not part of
 * the upload control itself).
 */
export interface CaseDocumentPendingSubmit {
  fileName: string;
  disabled: boolean;
  confirmTitle: string;
  confirmContent: string;
  onConfirm: () => Promise<void>;
}

/** The unsigned sub-step takes priority - it must be confirmed before the signed one is reachable. */
export const getPendingSubmit = (
  slot: CaseDocumentChainSlot | undefined,
): CaseDocumentPendingSubmit | undefined => {
  if (!slot) return undefined;

  if (slot.unsignedFile && !slot.isUnsignedConfirmed) {
    return {
      fileName: slot.unsignedFile.fileName ?? "",
      disabled: slot.unsignedPermissionMode !== "upload-delete",
      confirmTitle: "Submit this document?",
      confirmContent: `Confirm that "${slot.unsignedFile.fileName}" is the correct file. This cannot be undone.`,
      onConfirm: slot.confirmUnsigned,
    };
  }

  if (!slot.singleUploadOnly && slot.signedFile && !slot.isSignedConfirmed) {
    return {
      fileName: slot.signedFile.fileName ?? "",
      disabled: slot.signedPermissionMode !== "upload-delete",
      confirmTitle: "Submit this document?",
      confirmContent: `Confirm that "${slot.signedFile.fileName}" is the correct signed copy. This locks it from deletion and cannot be undone.`,
      onConfirm: slot.confirmSigned,
    };
  }

  return undefined;
};

/**
 * A slot is complete once its unsigned copy is confirmed and - unless it's a single-upload-only
 * slot like Case Summary, which has no acknowledge/signed sub-step at all - the member has
 * acknowledged and the signed copy is also confirmed.
 */
const isSlotComplete = (slot: CaseDocumentChainSlot): boolean =>
  slot.singleUploadOnly
    ? slot.isUnsignedConfirmed
    : slot.isUnsignedConfirmed && slot.isAcknowledged && slot.isSignedConfirmed;

export const confirmCaseDocumentSubmit = (
  pending: CaseDocumentPendingSubmit,
) => {
  dialogBox.confirm({
    title: pending.confirmTitle,
    content: <>{pending.confirmContent}</>,
    actions: {
      confirm: {
        text: "Yes, submit",
        svgIcon: checkIcon,
        onClick: pending.onConfirm,
      },
      cancel: { text: "No, cancel" },
    },
  });
};

/**
 * One TabStripTab's content: the unsigned (LRD)/acknowledge/signed (Coordinator) sub-steps for a
 * single case-document slot (Task 157786). Uses its own small local react-hook-form instance
 * purely to satisfy `ControlledFileUploader`'s RHF binding contract for display purposes - the
 * actual source of truth is `slot`, refetched from the server after every mutation.
 */
const CaseDocumentSlotTab = ({
  slot,
  incidentRecordId,
  loading,
}: {
  slot: CaseDocumentChainSlot;
  incidentRecordId: string | undefined;
  loading?: boolean;
}) => {
  const { control, setValue } = useForm<SlotFormValues>({
    defaultValues: emptySlotValues,
  });

  useEffect(() => {
    setValue("unsignedFile", slot.unsignedFile ?? null);
    setValue("signedFile", slot.signedFile ?? null);
    setValue("acknowledged", slot.isAcknowledged);
  }, [slot.unsignedFile, slot.signedFile, slot.isAcknowledged, setValue]);

  const showAcknowledgeAndSigned = !slot.singleUploadOnly;

  const getFileUrl = (file: IncidentRecordFileInfoDto) =>
    incidentRecordId && file.id
      ? resolveIncidentAttachmentUrl(incidentRecordId, file.id, file.fileName ?? "")
      : undefined;
  const getFileName = (file: IncidentRecordFileInfoDto) => file.fileName ?? "";

  return (
    <FormFieldSet cols={3}>
      <ControlledFileUploader
        control={control}
        name="unsignedFile"
        // Bug 157936: Case Summary (Major Case's only singleUploadOnly slot - no ack/signed
        // sub-steps) showed the generic "Unsigned copy (optional)" label like every other
        // document type, which read oddly for a document that isn't a signed/unsigned pair at
        // all. Use the slot's own label ("Case Summary") for singleUploadOnly slots instead.
        label={
          (slot.singleUploadOnly ? slot.label : "Unsigned copy") +
          (slot.isUnsignedConfirmed ? " ✓" : "")
        }
        loading={loading}
        permissionMode={slot.unsignedPermissionMode}
        restrictions={{
          allowedExtensions: CASE_DOCUMENT_ALLOWED_EXTENSIONS,
          maxFileSize: CASE_DOCUMENT_MAX_SIZE_BYTES,
        }}
        getFileName={getFileName}
        getFileUrl={getFileUrl}
        onUpload={(file, onProgress) =>
          slot.uploadUnsigned(file, onProgress).then((r) => ({ fileName: r.name }))
        }
        onDelete={() => slot.deleteUnsigned()}
      />

      {showAcknowledgeAndSigned ? (
        <ControlledCheckbox
          name="acknowledged"
          control={control}
          label="Member Acknowledge?"
          disabled={!slot.canEditAcknowledge}
          onChange={(value) => slot.setAcknowledged(Boolean(value))}
        />
      ) : (
        <div />
      )}

      {showAcknowledgeAndSigned ? (
        <ControlledFileUploader
          control={control}
          name="signedFile"
          label={"Signed copy *" + (slot.isSignedConfirmed ? " ✓" : "")}
          loading={loading}
          permissionMode={slot.signedPermissionMode}
          restrictions={{
            allowedExtensions: CASE_DOCUMENT_ALLOWED_EXTENSIONS,
            maxFileSize: CASE_DOCUMENT_MAX_SIZE_BYTES,
          }}
          getFileName={getFileName}
          getFileUrl={getFileUrl}
          onUpload={(file, onProgress) =>
            slot.uploadSigned(file, onProgress).then((r) => ({ fileName: r.name }))
          }
          onDelete={() => slot.deleteSigned()}
        />
      ) : (
        <div />
      )}
    </FormFieldSet>
  );
};

/**
 * Renders the case-document chain (Task 157786) as a Kendo TabStrip, one tab per document type,
 * replacing the vertical stack of bordered blocks the previous design used. Tab enablement follows
 * `slot.tabEnabled` from `useCaseDocumentChain`. The Submit action for the active tab's pending
 * sub-step lives in the parent page's FormActionsBar (see `getPendingSubmit`), not inline here -
 * per BA feedback, Submit is a page-level action alongside Save/Complete/Back, not part of the
 * upload control itself. `selected`/`onSelect` are lifted to the parent so it knows which slot's
 * pending action to render there.
 */
interface CaseDocumentTabStripProps {
  slots: CaseDocumentChainSlot[];
  selected: number;
  onSelect: (index: number) => void;
  incidentRecordId: string | undefined;
  /** See ControlledFileUploaderProps.loading. */
  loading?: boolean;
}

const CaseDocumentTabStrip = ({
  slots,
  selected,
  onSelect,
  incidentRecordId,
  loading,
}: CaseDocumentTabStripProps) => {
  if (slots.length === 0) return null;

  return (
    <TabStrip
      selected={selected}
      onSelect={(e) => onSelect(e.selected)}
      scrollable
      scrollButtons="visible"
      size="large"
      renderAllContent
      tabAlignment="stretched"
    >
      {slots.map((slot) => (
        <TabStripTab
          key={slot.key}
          title={
            isSlotComplete(slot) ? (
              <>
                <SvgIcon icon={checkIcon} /> {slot.label}
              </>
            ) : (
              slot.label
            )
          }
          disabled={!slot.tabEnabled}
        >
          <CaseDocumentSlotTab
            slot={slot}
            incidentRecordId={incidentRecordId}
            loading={loading}
          />
        </TabStripTab>
      ))}
    </TabStrip>
  );
};

export default CaseDocumentTabStrip;
